import React, { useState } from 'react';
import cx from 'classnames';

import { Hyperlink } from '@motorway/mw-highway-code/alpha';

import styles from './Tabs.scss';

export type TabItem = {
  label: string;
  panel: React.ReactNode;
};

export type TabsProps = {
  className?: string;
  idPrefix?: string;
  tabs: TabItem[];
};

export const Tabs: React.FC<TabsProps> = ({ className, idPrefix = 'tabs', tabs }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  if (tabs.length === 0) {
    return null;
  }

  const handleClick = (index: number) => () => {
    setActiveIndex(index);
    document.getElementById(`${idPrefix}-tab-${index}`)?.focus();
  };

  const handleKeyDown = (index: number) => (e: React.KeyboardEvent) => {
    const count = tabs.length;
    let targetIndex: number | null = null;

    switch (e.key) {
    case 'ArrowRight':
      targetIndex = (index + 1) % count;
      break;
    case 'ArrowLeft':
      targetIndex = (index - 1 + count) % count;
      break;
    case 'Enter':
    case ' ':
      e.preventDefault();
      setActiveIndex(index);
      return;
    default:
      return;
    }

    e.preventDefault();
    document.getElementById(`${idPrefix}-tab-${targetIndex}`)?.focus();
  };

  return (
    <div className={cx(styles.tabsContainer, className)}>
      <ul className={styles.tablist} data-testid={`${idPrefix}-tablist`} role="tablist">
        {tabs.map((tab, i) => {
          const isActive = i === activeIndex;
          return (
            <li key={`${idPrefix}-tab-${i}`}
              aria-selected={isActive}
              className={cx(styles.tab, { [styles.active]: isActive })}
              role="tab"
            >
              <Hyperlink
                inherit
                aria-controls={`${idPrefix}-panel-${i}`}
                as="button"
                className={cx(styles.tabButton)}
                data-testid={`${idPrefix}-tab-${i}`}
                id={`${idPrefix}-tab-${i}`}
                label={tab.label}
                onClick={handleClick(i)}
                onKeyDown={handleKeyDown(i)}
                underline={false}
                variant={isActive ? 'primary' : 'secondary'}
              />
            </li>
          );
        })}
      </ul>
      {tabs.map((tab, i) => {
        const isActive = i === activeIndex;
        return (
          <div
            key={`${idPrefix}-tab-${i}`}
            aria-hidden={!isActive || undefined}
            aria-labelledby={`${idPrefix}-tab-${i}`}
            className={cx(styles.panel, { [styles.panelActive]: isActive })}
            data-testid={`${idPrefix}-panel-${i}`}
            id={`${idPrefix}-panel-${i}`}
            role="tabpanel"
          >
            {tab.panel}
          </div>
        );
      })}
    </div>
  );
};
